import {
	BidSuretyBondClosedReason,
	ContractSuretyType,
	Dtos,
	FinalContractSuretyBondClosedReason,
	SuretyAccountId,
	SuretyBondId,
	SuretyBondType,
	SuretyType,
	toISODateTime,
} from "@inrev/common";
import { useCloseBond, useRequestCloseBond } from "@portal/api/bond/agent";
import { FormComponent, FormFieldGroup } from "@portal/components/form";
import {
	FormCurrencyComponent,
	FormDateComponent,
	FormDropdownComponent,
} from "@portal/components/form/fieldLayouts";
import { FormTextArea } from "@portal/components/form/fields";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { Icon } from "@portal/components/primitives";
import { Banner } from "@portal/components/primitives/Banner";
import { Button } from "@portal/components/primitives/Button";
import { DateTime } from "luxon";
import { ReactNode, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { match } from "ts-pattern";
import { bondTypeLabelMap } from "../../../../constants";
import { stripEmptyResolver, typedZodResolver } from "../../../../utils/form";

type CloseBondModalProps = {
	bond: {
		id: SuretyBondId;
		accountId: SuretyAccountId;
		suretyType: SuretyType;
		contractSuretyType: ContractSuretyType;
		bondType: SuretyBondType;
		amount: number;
		number: string;
	};
	button: ReactNode;
};

type CloseBondFormData = {
	suretyType: SuretyType;
	contractSuretyType: ContractSuretyType;
	closedReason: FinalContractSuretyBondClosedReason | BidSuretyBondClosedReason | "";
	closedNote: string | "";
	actualCompletionDate: string | "";
	actualContractAmount: string | "";
};

const getDefaultFormData = (bond: CloseBondModalProps["bond"]): CloseBondFormData => ({
	suretyType: bond.suretyType,
	contractSuretyType: bond.contractSuretyType,
	closedReason: "",
	closedNote: "",
	actualCompletionDate: "",
	actualContractAmount: "",
});

const getClosedReasonOptions = (suretyBondType: SuretyBondType) => {
	return match(suretyBondType as SuretyBondType)
		.with(SuretyBondType.bid, () => [
			{ value: BidSuretyBondClosedReason.bid_lost, label: "Bid lost" },
			{ value: BidSuretyBondClosedReason.not_awarded, label: "Project was not awarded" },
			{
				value: FinalContractSuretyBondClosedReason.returned_by_obligee,
				label: "Returned by obligee",
			},
			{ value: BidSuretyBondClosedReason.other, label: "Other" },
		])
		.with(SuretyBondType.final, () => [
			{ value: FinalContractSuretyBondClosedReason.project_completed, label: "Project completed" },
			{
				value: FinalContractSuretyBondClosedReason.returned_by_obligee,
				label: "Returned by obligee",
			},
			{ value: FinalContractSuretyBondClosedReason.other, label: "Other" },
		])
		.otherwise(() => []);
};

export const CloseBondModal = ({ bond, button }: CloseBondModalProps) => {
	const { closeBond, closeBondLoading } = useCloseBond(bond.id, bond.accountId);
	const { requestCloseBond, requestCloseBondLoading } = useRequestCloseBond(bond.id);
	const [isOpen, setIsOpen] = useState(false);
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	const formMethods = useForm<CloseBondFormData, any, Dtos.SuretyBond.Close.Request>({
		defaultValues: getDefaultFormData(bond),
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(typedZodResolver(Dtos.SuretyBond.Close.Request.schema)),
	});
	const closedReasonOptions = useMemo(() => getClosedReasonOptions(bond.bondType), [bond.bondType]);
	const closedReason = formMethods.watch("closedReason");
	const canCloseFinalBond = useMemo(
		() => bond.bondType !== "final" || bond.amount <= 1000000,
		[bond.bondType, bond.amount],
	);
	const isCloseRequest = () => {
		return closedReason === "other" || !canCloseFinalBond;
	};

	const handleSubmit = (data: Dtos.SuretyBond.Close.Request) => {
		if (isCloseRequest()) {
			requestCloseBond(data);
		} else {
			closeBond(data);
		}
		setIsOpen(false);
	};

	return (
		<>
			<span onClick={() => setIsOpen(true)}>{button}</span>
			{isOpen && (
				<Modal onClickOutside={() => setIsOpen(false)}>
					<ModalItemWithHeader
						header={`Close ${bondTypeLabelMap[bond.bondType]} Bond ${bond.number}`}
						className="w-[500px]"
						bodyClassName="p-[30px] pt-[26px]"
						onClose={() => setIsOpen(false)}
					>
						<form>
							<FormFieldGroup>
								{bond.bondType === "final" && bond.amount > 1000000 && (
									<Banner tone="info">
										<Banner.Leading>
											<Icon
												type="information-circle"
												size="medium"
												className="text-brand-accent stroke-[1.75]"
											/>
										</Banner.Leading>
										<Banner.Content>
											The inRev Agent Portal does not yet support automatically closing Final Bonds
											over $1,000,000. An inRev underwriter will close the bond after reviewing your
											request.
										</Banner.Content>
									</Banner>
								)}
								<FormDropdownComponent
									formMethods={formMethods}
									name="closedReason"
									label="Reason for Closing"
									placeholder="Select one"
									options={closedReasonOptions}
								/>
								{closedReason === FinalContractSuretyBondClosedReason.project_completed && (
									<>
										<FormDateComponent
											formMethods={formMethods}
											name="actualCompletionDate"
											label="Project Completion Date"
											disableAfter={toISODateTime(DateTime.now().endOf("day"))}
										/>
										<FormCurrencyComponent
											formMethods={formMethods}
											name="actualContractAmount"
											label="Contract Amount at Project Completion"
										/>
									</>
								)}
								{(closedReason === "returned_by_obligee" || closedReason === "other") && (
									<>
										{canCloseFinalBond && closedReason === "other" && (
											<Banner tone="info">
												<Banner.Leading>
													<Icon
														type="information-circle"
														size="medium"
														className="text-brand-accent stroke-[1.75]"
													/>
												</Banner.Leading>
												<Banner.Content>
													When selecting "Other", the bond will not be closed immediately. An inRev
													underwriter will close the bond after reviewing your request.
												</Banner.Content>
											</Banner>
										)}
										<FormComponent
											formMethods={formMethods}
											name="closedNote"
											label={
												closedReason === "returned_by_obligee"
													? "Please describe why the bond was returned by the obligee"
													: "Please describe the reason for closing the bond"
											}
										>
											<FormTextArea
												control={formMethods.control}
												name="closedNote"
												placeholder="Enter description here"
												className="h-[100px]"
											/>
										</FormComponent>
									</>
								)}
							</FormFieldGroup>
							<div className="flex justify-center mt-[35px] gap-2">
								<Button color="secondary" variant="outline" onClick={() => setIsOpen(false)}>
									Cancel
								</Button>
								<Button
									color="primary"
									disabled={closedReason === ""}
									leadingIcon={<Icon type="shield-slash" className="stroke-[1]" size="mini" />}
									onClick={formMethods.handleSubmit(handleSubmit)}
								>
									{isCloseRequest() ? "Submit Close Request" : "Close Bond"}
								</Button>
							</div>
						</form>
					</ModalItemWithHeader>
				</Modal>
			)}
			{(closeBondLoading || requestCloseBondLoading) && !isOpen && <LoadingModal />}
		</>
	);
};
